import type p5 from 'p5';
import { Player, PlayerState, InputAction } from './player-types';
import { getP5 } from '../../../core/p5-instance';
import {
    getSelectedShip,
    getLanes,
    setGameState,
    getDeathAnimTimer,
    setDeathAnimTimer,
    getStageStartTimer,
    setStageStartTimer,
    getPlayerStateMachine,
    setPlayerEntryY,
    setBoosterAlpha,
    setEngineMoveDirection,
    getSideBoosterLeftTimer,
    setSideBoosterLeftTimer,
    getSideBoosterRightTimer,
    setSideBoosterRightTimer,
    getPrevMoveDirection,
    setPrevMoveDirection,
    setPanelSlideProgress,
    getLives,
    getDyingFadeAlpha,
    setDyingFadeAlpha,
    getEnemies,
    setEnemies,
    getEnemyBullets,
    setEnemyBullets,
    getBullets,
    setBullets,
    setItems,
    setBossPaused
} from '../../../store/index';
import { shoot, useSpecialAbility } from '../player';
import { CONFIG } from '../../../config/game-config';
import { calculateEntryAnimation } from '../animations/player-animations';

/**
 * updateSideBoosters() - サイドバーニアタイマーの更新
 * 移動開始時にサイドバーニアを発火
 * @param moveDirection - 現在の移動方向
 */
function updateSideBoosters(moveDirection: number): void {
    const prevDir = getPrevMoveDirection();
    const currentDir = Math.sign(moveDirection);
    const boosterDuration = 12; // バーニア持続時間（フレーム）

    // 移動方向が変化した時にサイドバーニアを発火
    // 右に移動開始 → 左側バーニア（反作用）
    // 左に移動開始 → 右側バーニア（反作用）
    if (currentDir !== prevDir) {
        if (currentDir > 0) {
            // 右移動開始 → 左サイドバーニア
            setSideBoosterLeftTimer(boosterDuration);
        } else if (currentDir < 0) {
            // 左移動開始 → 右サイドバーニア
            setSideBoosterRightTimer(boosterDuration);
        }
    }

    // 前フレームの方向を更新
    setPrevMoveDirection(currentDir);

    // タイマーを減少
    const leftTimer = getSideBoosterLeftTimer();
    const rightTimer = getSideBoosterRightTimer();
    if (leftTimer > 0) {
        setSideBoosterLeftTimer(leftTimer - 1);
    }
    if (rightTimer > 0) {
        setSideBoosterRightTimer(rightTimer - 1);
    }
}

/**
 * 共通の基底クラス
 */
abstract class BasePlayerState implements PlayerState {
    abstract readonly name: string;
    enter(_player: Player): void {}
    exit(_player: Player): void {}
    update(_player: Player, _dt: number): void {}
    draw(_player: Player, _p: p5): void {}
    handleAction(_player: Player, _action: InputAction): void {}
}

/**
 * 通常状態: プレイヤーが操作可能な状態
 */
export class NormalState extends BasePlayerState {
    readonly name = 'Normal';

    update(player: Player, dt: number): void {
        const p = getP5();
        const selectedShip = getSelectedShip();
        const shipCfg = CONFIG.ships[selectedShip] || CONFIG.ships['bomber'];

        // 無敵タイマー・点滅タイマーのカウントダウン
        if (player.invincibleTimer && player.invincibleTimer > 0) {
            player.invincibleTimer -= dt;
            if (player.invincibleTimer <= 0) {
                player.invincibleTimer = 0;
            }
        }
        if (player.flashTimer && player.flashTimer > 0) {
            player.flashTimer -= dt;
            if (player.flashTimer <= 0) {
                player.flashTimer = 0;
            }
        }

        // lerp前の差分を保存（エンジンアニメーション用）
        const moveDirection = player.targetX - player.x;
        setEngineMoveDirection(moveDirection);

        // サイドバーニア状態の更新
        updateSideBoosters(moveDirection);

        // 目標位置に向かってスムーズに移動
        const moveSpeed = 0.2 * shipCfg.moveSpeed;
        const lerpFactor = 1 - Math.pow(1 - moveSpeed, dt);
        player.x = p.lerp(player.x, player.targetX, lerpFactor);
    }

    handleAction(player: Player, action: InputAction): void {
        const lanes = getLanes();

        switch (action) {
            case 'moveLeft':
                if (player.lane > 0) {
                    player.lane--;
                    player.targetX = lanes[player.lane];
                }
                break;
            case 'moveRight':
                if (player.lane < CONFIG.laneCount - 1) {
                    player.lane++;
                    player.targetX = lanes[player.lane];
                }
                break;
            case 'shoot':
                shoot(); // 単発クリック時も射撃
                break;
            case 'special':
                useSpecialAbility();
                break;
        }
    }
}

/**
 * 非アクティブ状態: タイトル画面などプレイヤーが存在しない状態
 */
export class InactiveState extends BasePlayerState {
    readonly name = 'Inactive';

    enter(player: Player): void {
        player.active = false;
    }
}

/**
 * リスポーン出撃状態: 画面下から定位置に移動するシンプルな出撃モーション
 * 登場アニメーション中（約60F）はプレイヤー操作を受け付けない
 */
export class RespawnEntryState extends BasePlayerState {
    readonly name = 'RespawnEntry';
    private timer: number = 0;
    private readonly duration: number = 60;

    enter(player: Player): void {
        const p = getP5();
        const lanes = getLanes();
        player.active = true;
        player.lane = 0;
        player.x = lanes[0];
        player.targetX = lanes[0];
        player.y = p.height + 50;
        player.tilt = 0;
        player.invincibleTimer = 120;
        player.flashTimer = 120;
        this.timer = this.duration;
        setBoosterAlpha(200);
        setPlayerEntryY(player.y);
    }

    update(player: Player, dt: number): void {
        this.timer -= dt;
        const progress = 1 - (this.timer / this.duration);
        const eased = 1 - Math.pow(1 - progress, 3);

        const p = getP5();
        const startY = p.height + 50;
        const endY = CONFIG.stageStart.playerEndY;
        player.y = p.lerp(startY, endY, eased);
        player.tilt = 0;
        setPlayerEntryY(player.y);
        setBoosterAlpha(200 * (1 - eased));

        if (this.timer <= 0) {
            const lanes = getLanes();
            player.y = endY;
            player.lane = 0;
            player.targetX = lanes[0];
            player.x = lanes[0];
            setBossPaused(false);
            getPlayerStateMachine().transitionTo(new NormalState());
        }
    }
}

/**
 * 待機状態: 死亡演出後、画面上の敵/弾がクリアされるのを待つ状態
 * 通常敵: 全敵＋全敵弾が画面外に消えたら再出撃
 * ボス戦: ボスは停止、敵弾が消えたら再出撃
 */
export class WaitingState extends BasePlayerState {
    readonly name = 'Waiting';

    enter(player: Player): void {
        player.active = false;

        // プレイヤー弾をクリア
        getBullets().length = 0;

        // ボスが存在する場合は一時停止
        const enemies = getEnemies();
        if (enemies.some(e => e.isBoss)) {
            setBossPaused(true);
        }
    }

    update(_player: Player, _dt: number): void {
        const enemies = getEnemies();
        const enemyBullets = getEnemyBullets();
        const bossExists = enemies.some(e => e.isBoss);

        if (bossExists) {
            // ボス戦: 敵弾がすべて消えたら再出撃
            if (enemyBullets.length === 0) {
                getPlayerStateMachine().transitionTo(new RespawnEntryState());
            }
        } else {
            // 通常: 全敵＋全敵弾が消えたら再出撃
            if (enemies.length === 0 && enemyBullets.length === 0) {
                getPlayerStateMachine().transitionTo(new RespawnEntryState());
            }
        }
    }
}

/**
 * 死亡状態: 被弾して爆発演出中の状態
 */
export class DyingState extends BasePlayerState {
    readonly name = 'Dying';
    private entitiesCleared = false;
    private fadeDelay = 0;
    private hasLivesRemaining = false;

    enter(player: Player): void {
        player.active = true;
        this.entitiesCleared = false;
        this.hasLivesRemaining = getLives() > 0;

        // 残機ありの場合、フェードアウト遅延を設定
        if (this.hasLivesRemaining) {
            setDyingFadeAlpha(1.0);
            this.fadeDelay = 23; // フェード開始までの待機フレーム
        }
    }

    update(_player: Player, dt: number): void {
        // フェードアウト処理（残機ありの場合のみ）
        if (this.hasLivesRemaining && !this.entitiesCleared) {
            // フェード開始前の遅延
            if (this.fadeDelay > 0) {
                this.fadeDelay -= dt;
            } else {
                const FADE_FRAMES = 45;
                const alpha = getDyingFadeAlpha();
                if (alpha > 0) {
                    setDyingFadeAlpha(Math.max(0, alpha - dt / FADE_FRAMES));
                }
                // フェード完了時にエンティティをクリア（ボス以外）
                if (getDyingFadeAlpha() <= 0) {
                    setEnemies(getEnemies().filter(e => e.type === 'boss'));
                    setEnemyBullets([]);
                    setBullets([]);
                    setItems([]);
                    this.entitiesCleared = true;
                }
            }
        }

        let timer = getDeathAnimTimer();
        timer -= dt;
        setDeathAnimTimer(timer);

        if (timer <= 0) {
            if (this.hasLivesRemaining) {
                // 残機あり: 不透明に戻して画面クリア待ちへ遷移
                setDyingFadeAlpha(1.0);
                getPlayerStateMachine().transitionTo(new WaitingState());
            } else {
                // 残機なし: ゲームオーバー
                setGameState('gameover');
                getPlayerStateMachine().transitionTo(new InactiveState());
            }
        }
    }

    draw(_player: Player, _p: p5): void {
        // 死亡中は機体を描画しない（ship-renderer側で制御）
    }
}

/**
 * 登場演出状態: ステージ開始時に画面外から登場する状態
 */
export class EntryState extends BasePlayerState {
    readonly name = 'Entry';

    enter(player: Player): void {
        player.active = true;
        player.tilt = 0;  // 傾きをリセット
        setBoosterAlpha(255);  // ブースター全開で開始
        setSideBoosterLeftTimer(0);
        setSideBoosterRightTimer(0);
        setPrevMoveDirection(0);
    }

    update(player: Player, _dt: number): void {
        // 登場演出中は傾きを0に保つ
        player.tilt = 0;
        const p = getP5();
        const lanes = getLanes();

        // ステージ開始タイマーを減らす
        let timer = getStageStartTimer();
        timer -= _dt;
        setStageStartTimer(timer);

        const progress = 1 - (timer / CONFIG.stageStart.duration);

        // パネルスライドイン更新（40フレームで完了）
        const PANEL_SLIDE_DURATION = 40;
        const elapsed = CONFIG.stageStart.duration - timer;
        setPanelSlideProgress(Math.min(1.0, elapsed / PANEL_SLIDE_DURATION));

        // アニメーション計算を委譲
        const anim = calculateEntryAnimation(
            progress,
            p.width / 2,
            lanes,
            CONFIG.stageStart.playerStartY,
            CONFIG.stageStart.playerMidY,
            CONFIG.stageStart.playerEndY,
            p
        );

        // 座標を適用
        player.x = anim.x;
        player.y = anim.y;
        setPlayerEntryY(anim.y);

        // ブースター強度を設定（中央停止時にフェードアウト）
        setBoosterAlpha(anim.boosterAlpha);

        // 演出完了後、Normal状態へ遷移
        if (timer <= 0) {
            player.lane = 0;
            player.targetX = lanes[0];
            player.x = lanes[0];
            player.y = CONFIG.stageStart.playerEndY;
            getPlayerStateMachine().transitionTo(new NormalState());
        }
    }
}
